import React, { useState } from 'react';
import { Award, ArrowRight, ArrowLeft } from 'lucide-react';
import { 
  CertifiedNetworkApplication, 
  PRE_AUTHORIZATION_SUBJECTS,
  AVAILABLE_SERVICES 
} from '../../services/certifiedNetworkService';
import LoadingButton from '../LoadingButton';

interface Step4AuthorizationsProps {
  data: Partial<CertifiedNetworkApplication>;
  onComplete: (data: any) => void;
  onBack: () => void;
}

const Step4Authorizations: React.FC<Step4AuthorizationsProps> = ({ data, onComplete, onBack }) => {
  // Inizializza preAuthorizations con tutte le materie disponibili
  const initializePreAuthorizations = () => {
    if (data.preAuthorizations && data.preAuthorizations.length > 0) {
      return data.preAuthorizations;
    }
    // Se non ci sono dati, crea l'array con tutte le materie disponibili
    return PRE_AUTHORIZATION_SUBJECTS.map(materia => ({
      materia,
      selected: false
    }));
  };

  const [formData, setFormData] = useState({
    preAuthorizations: initializePreAuthorizations(),
    services: data.services || [],
    ebas: data.ebas || {
      versaDiritti: false
    }
  });

  const togglePreAuthorization = (index: number) => {
    setFormData(prev => ({
      ...prev,
      preAuthorizations: prev.preAuthorizations.map((item, i) =>
        i === index ? { ...item, selected: !item.selected } : item
      )
    }));
  };

  const toggleService = (service: string) => {
    setFormData(prev => {
      const newServices = prev.services.includes(service)
        ? prev.services.filter(s => s !== service)
        : [...prev.services, service];

      console.log('Toggle service:', service, 'New services:', newServices);

      return {
        ...prev,
        services: newServices
      };
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onComplete(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      <div className="text-center mb-6">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-orange-100 dark:bg-orange-900/50 rounded-full mb-4">
          <Award className="w-8 h-8 text-orange-600 dark:text-orange-400" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Autorizzazioni e Servizi
        </h2>
        <p className="text-gray-600 dark:text-gray-300">
          Seleziona le materie per cui richiedi la pre-autorizzazione e i servizi offerti.
        </p>
      </div>

      {/* Sezione 8: Pre-Autorizzazioni */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white border-b dark:border-gray-700 pb-2">
          8. Pre-Autorizzazione per Materie
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-300">
          Seleziona una o più materie per cui richiedi la pre-autorizzazione:
        </p>

        <div className="grid md:grid-cols-2 gap-3">
          {formData.preAuthorizations.map((item, index) => (
            <div
              key={index}
              className={`border rounded-lg p-4 cursor-pointer transition-all ${
                item.selected
                  ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                  : 'border-gray-300 dark:border-gray-600 hover:border-green-300'
              }`}
              onClick={() => togglePreAuthorization(index)}
            >
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={item.selected}
                  onChange={(e) => {
                    e.stopPropagation();
                    togglePreAuthorization(index);
                  }}
                  onClick={(e) => e.stopPropagation()}
                  className="w-4 h-4 text-green-600 border-gray-300 dark:border-gray-600 rounded focus:ring-green-500"
                />
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer">
                  {item.materia}
                </label>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mt-4">
          <p className="text-sm text-blue-800 dark:text-blue-200">
            <strong>Impegno:</strong> Ti impegni a partecipare, entro i 24 mesi successivi 
            all'ammissione e comunque non oltre il 31 dicembre 2028, ai corsi necessari per 
            ottenere la certificazione definitiva nella materia selezionata.
          </p>
        </div>
      </div>

      {/* Sezione 11: EBAS */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white border-b dark:border-gray-700 pb-2">
          11. EBAS
        </h3>

        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            id="versaDiritti"
            checked={formData.ebas.versaDiritti}
            onChange={(e) => setFormData(prev => ({
              ...prev,
              ebas: { versaDiritti: e.target.checked }
            }))}
            className="w-4 h-4 text-green-600 border-gray-300 dark:border-gray-600 rounded focus:ring-green-500"
          />
          <label htmlFor="versaDiritti" className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Lo Studio versa i diritti stabiliti al fondo EBAS per i dipendenti
          </label>
        </div>
      </div>

      {/* Sezione 12: Servizi Offerti */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white border-b dark:border-gray-700 pb-2">
          12. Servizi Offerti dallo Studio
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-300">
          Seleziona i principali servizi offerti dal tuo Studio di Consulenza:
        </p>

        <div className="grid md:grid-cols-2 gap-3">
          {AVAILABLE_SERVICES.map((service, index) => (
            <div
              key={index}
              className={`border rounded-lg p-4 cursor-pointer transition-all ${
                formData.services.includes(service)
                  ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                  : 'border-gray-300 dark:border-gray-600 hover:border-green-300'
              }`}
              onClick={() => toggleService(service)}
            >
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={formData.services.includes(service)}
                  onChange={(e) => {
                    e.stopPropagation();
                    toggleService(service);
                  }}
                  onClick={(e) => e.stopPropagation()}
                  className="w-4 h-4 text-green-600 border-gray-300 dark:border-gray-600 rounded focus:ring-green-500"
                />
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer">
                  {service}
                </label>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Buttons */}
      <div className="flex justify-between pt-6 border-t border-gray-200 dark:border-gray-700">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-2 px-6 py-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
        >
          <ArrowLeft className="w-5 h-5" />
          Indietro
        </button>
        <LoadingButton
          type="submit"
          className="flex items-center gap-2 bg-green-600 text-white px-8 py-2 rounded-lg hover:bg-green-700"
        >
          Continua
          <ArrowRight className="w-5 h-5" />
        </LoadingButton>
      </div>
    </form>
  );
};

export default Step4Authorizations;

